// components/Backtest/runningJobs.js — pure helpers over the Running list
// (GET /api/backtest/active `jobs`, ui/data useRunningJobsQuery). Kept out of
// useDraftTest.js so a spec that mocks the hook still gets these.

/**
 * The running job (GET /api/backtest/active `jobs`) a page should re-attach
 * to: one for this draft, else — for a scanner BOOK — one for this scanner.
 * `kind` narrows it ('book' | 'draft'). Null when nothing matches.
 */
export function runningJobFor(jobs, { draftId = null, scannerId = null, kind = null } = {}) {
  const list = Array.isArray(jobs) ? jobs : [];
  const ok = (j) => j && j.jobId && (!kind || j.kind === kind);
  return (draftId != null && list.find((j) => ok(j) && j.draftId != null && String(j.draftId) === String(draftId)))
    || (scannerId != null && list.find((j) => ok(j) && j.scannerId != null && String(j.scannerId) === String(scannerId)))
    || null;
}

/**
 * The sweep / walk-forward record id inside a job id: the api builds both as
 * `backtest:<uid>:<botId|sweep|wf>:<searchId>` (routes/API.js startSweep,
 * controllers/backtestAnalysis.js startWalkForward). Null when there is none.
 */
export function searchIdOfJob(jobId) {
  const parts = String(jobId || '').split(':');
  return parts.length >= 4 && parts[0] === 'backtest' && parts[parts.length - 1] ? parts[parts.length - 1] : null;
}

/**
 * The one calm line while a book waits for a busy market-data lake:
 * useDraftTest's `waiting = { message, waitedMs, budgetMs }` — the api's own
 * status line when it sent one, else the minutes waited of the budget. Null
 * when the machine has no `waiting` (not waiting, or an older hook). Shared by
 * pages/Scanners/BookTest and components/Research/ScannerBookTest.
 */
export function waitingLine(machine) {
  const w = machine && machine.waiting;
  if (!w || typeof w !== 'object') return null;
  const msg = typeof w.message === 'string' ? w.message.trim() : '';
  if (msg) return msg;
  const fin = (v) => (v === null || v === undefined || v === '' || typeof v === 'object' || !Number.isFinite(Number(v)) ? null : Number(v));
  const waited = fin(w.waitedMs);
  if (waited === null) return 'Waiting for the market-data lake (busy) — retrying.';
  const budget = fin(w.budgetMs);
  const min = (ms) => Math.max(0, Math.round(ms / 60000));
  return `Waiting for the market-data lake (busy) — retrying, ${min(waited)}${budget !== null ? ` of ${min(budget)}` : ''} min`;
}

/**
 * A scanner book's preparation step in plain words (api `stage` on the status
 * poll and on GET /api/backtest/active, backtest/scannerBookPrepare.js). Null
 * for 'simulating' — the progress bar says that — and for an absent or unknown
 * stage (an older api), so the caller keeps what it showed before.
 */
export const STAGE_LABELS = {
  preparing: 'Preparing the book…',
  resolving_universe: 'Resolving the universe…',
  pricing: 'Pricing the universe…',
};
export function stageLabel(stage) {
  return (typeof stage === 'string' && Object.prototype.hasOwnProperty.call(STAGE_LABELS, stage)) ? STAGE_LABELS[stage] : null;
}
